<template>
  <div class="tag-autocomplete-wrapper relative" ref="wrapper">
    <div class="input input-bordered w-full min-h-[3rem] flex flex-wrap gap-2 items-center px-3 py-2">
      <span
        v-for="tag in modelValue"
        :key="tag.id"
        class="inline-flex items-center gap-1 px-2 py-1 bg-primary bg-opacity-20 text-primary rounded text-sm"
      >
        {{ tag.name }}
        <button
          type="button"
          @click="removeTag(tag.id)"
          class="hover:bg-primary hover:bg-opacity-30 rounded-full p-0.5 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </span>

      <input
        ref="input"
        type="text"
        class="flex-1 min-w-[120px] bg-transparent outline-none border-none p-0 h-auto"
        v-model="searchQuery"
        :placeholder="modelValue.length === 0 ? placeholder : ''"
        @input="onInput"
        @focus="showDropdown = true"
        @keydown.down.prevent="navigateDown"
        @keydown.up.prevent="navigateUp"
        @keydown.enter.prevent="selectHighlighted"
        @keydown.escape="showDropdown = false"
        @keydown.backspace="handleBackspace"
        autocomplete="off"
      />
    </div>

    <div
      v-if="showDropdown && filteredOptions.length > 0"
      class="absolute z-50 w-full mt-1 bg-neutral rounded-lg shadow-lg max-h-60 overflow-y-auto"
    >
      <ul class="p-2">
        <li
          v-for="(option, index) in filteredOptions"
          :key="option.id"
          @mousedown.prevent="selectOption(option)"
          class="px-3 py-2 cursor-pointer rounded-md transition-colors"
          :class="index === highlightedIndex ? 'bg-primary bg-opacity-20' : 'hover:bg-base-300'"
        >
          <div class="flex items-center justify-between">
            <span v-html="highlightMatch(option.name)"></span>
            <svg v-if="isSelected(option.id)" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </li>
      </ul>
    </div>

    <div
      v-if="showDropdown && searchQuery && filteredOptions.length === 0"
      class="absolute z-50 w-full mt-1 bg-neutral rounded-lg shadow-lg p-3 text-center text-sm opacity-60"
    >
      Nenašli sa žiadne tagy
    </div>
  </div>
</template>

<script>
export default {
  name: 'TagAutocomplete',
  props: {
    modelValue: {
      type: Array,
      default: () => []
    },
    options: {
      type: Array,
      required: true
    },
    placeholder: {
      type: String,
      default: 'Začnite písať tag...'
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      searchQuery: '',
      showDropdown: false,
      highlightedIndex: 0
    }
  },
  computed: {
    filteredOptions() {
      if (!this.searchQuery) {
        return this.options
      }

      const query = this.searchQuery.toLowerCase()
      return this.options.filter(option =>
        option.name.toLowerCase().includes(query)
      )
    }
  },
  methods: {
    onInput() {
      this.showDropdown = true
      this.highlightedIndex = 0
    },
    isSelected(tagId) {
      return this.modelValue.some(tag => tag.id === tagId)
    },
    selectOption(option) {
      if (!this.isSelected(option.id)) {
        this.$emit('update:modelValue', [...this.modelValue, option])
      }
      this.searchQuery = ''
      this.showDropdown = false
      this.$refs.input.focus()
    },
    removeTag(tagId) {
      this.$emit('update:modelValue', this.modelValue.filter(tag => tag.id !== tagId))
    },
    navigateDown() {
      if (this.highlightedIndex < this.filteredOptions.length - 1) {
        this.highlightedIndex++
        this.scrollToHighlighted()
      }
    },
    navigateUp() {
      if (this.highlightedIndex > 0) {
        this.highlightedIndex--
        this.scrollToHighlighted()
      }
    },
    selectHighlighted() {
      if (this.filteredOptions[this.highlightedIndex]) {
        this.selectOption(this.filteredOptions[this.highlightedIndex])
      }
    },
    handleBackspace() {
      if (this.searchQuery === '' && this.modelValue.length > 0) {
        const lastTag = this.modelValue[this.modelValue.length - 1]
        this.removeTag(lastTag.id)
      }
    },
    scrollToHighlighted() {
      this.$nextTick(() => {
        const dropdown = this.$refs.wrapper?.querySelector('ul')
        const highlighted = dropdown?.children[this.highlightedIndex]
        if (highlighted) {
          highlighted.scrollIntoView({ block: 'nearest' })
        }
      })
    },
    highlightMatch(text) {
      if (!this.searchQuery) return text

      const regex = new RegExp(`(${this.searchQuery})`, 'gi')
      return text.replace(regex, '<mark class="bg-primary text-primary-content px-0.5 rounded">$1</mark>')
    },
    handleClickOutside(event) {
      if (this.$refs.wrapper && !this.$refs.wrapper.contains(event.target)) {
        this.showDropdown = false
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
  }
}
</script>

<style scoped>
.overflow-y-auto::-webkit-scrollbar {
  width: 8px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: hsl(var(--b1));
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: hsl(var(--b3));
  border-radius: 4px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: hsl(var(--bc) / 0.3);
}
</style>
